// Попытки старта и правки с неизвестным исходом. SMY-449, 06.10.2026
//
// Ответ на старт или правку потерян (`outcomeCause`) — экран досылает ту же
// попытку тем же ключом, пока показывает её чат. Ушёл человек из чата — попытку
// нельзя ни бросить (дойди первая, следующая отправка стала бы вторым
// запуском), ни досылать за его спиной (удачный повтор занял бы общий слот
// запуска на чужом экране, а новый чат увёл бы человека с экрана, на который он
// смотрит). Поэтому она встаёт на паузу здесь, у своего чата, и экран этого
// чата досылает её тем же ключом (решение владельца 06.10.2026, «пауза»).
// Попытки нового чата здесь нет и быть не может — у неё нет чата, на который
// вернуться: экран её снимает и перечитывает список чатов, чтобы дошедший
// запрос встал там своим чатом.
//
// Здесь же, а не в экранах, всё, что у кабинета и приложения общее (решение
// владельца 06.10.2026, «модуль»): склад попыток, цикл досылки, срок попытки и
// правило поля. Двое ворот ревью находили гонки в копиях этого кода по
// экранам, а проверить их там было нечем; здесь у каждого правила есть тест
// (`attempts.test.ts`).
//
// Попытка принадлежит сеансу, которым ушла (`session` — токен на момент первой
// отправки). Склад привязан к человеку и чистится при его смене, а попытка,
// чей сеанс сменился, не встаёт ни на экран, ни на паузу и не досылается:
// ответ первой отправки бывает в пути до тридцати секунд, и без этой сверки он
// приземлился бы уже под новым человеком — после продления гостя, выхода,
// входа в другой аккаунт или в соседней вкладке (ворота ревью 5, security).
// В памяти вкладки или приложения, а не в хранилище: перезагрузка попытку
// теряет, и строка на экране просит её не делать.

import { FIELD_AFTER_DRAFT, appendToField } from '@smyslika/core'

import { AttemptTimeout, outcomeCause, type AttemptCause } from './client.ts'
import { sentText, type StartRunInput } from './runs.ts'

/** Шаг досылки: столько экран ждёт между попытками, если сеть не вернулась раньше. */
export const ATTEMPT_STEP_MS = 10_000

/**
 * Сколько ждать ответа на одну попытку — и первую, и каждую досылку. Повисший
 * запрос — полуоткрытое соединение при смене сети — держал бы «Читаю задачу» и
 * «исход неизвестен» вечно: пока он в полёте, ни шаг, ни возврат сети новой
 * попытки не начинают (ворота ревью 06.10.2026, ux-states-checker). Оборванная
 * по сроку — `AttemptTimeout`, неизвестный исход с поводом «сервер не ответил».
 */
export const ATTEMPT_WAIT_MS = 30_000

export interface AttemptFile {
  id: string
  name: string
}

/**
 * Попытка, ответ на которую потерян. `chatId` — чат, где она живёт: `null` у
 * старта нового чата. `session` — сеанс, которым она ушла. `cause` — повод
 * последнего неизвестного исхода: по нему экран выбирает строку.
 *
 * У старта `field` и `file` — что стояло в поле при отправке; пусто у кнопки
 * под уточнениями. `cards` — объект карточек уточнений, чьи ответы ушли с
 * отправкой (`input.clarifications`, SMY-487), только для сверки «те же ли»
 * (`holdsCards`): он есть лишь у отправки, которая их несёт, — набранное при
 * карточках, кнопка под уточнениями, окно подтверждения. У бесплатного
 * уточнения и повтора сбоя ответов нет, и `cards` — `null` (ворота ревью 9). Что вернётся в
 * поле при отказе, решает `fieldOf`. `fromField` — уходил ли текст из поля:
 * удача снимет поле, уточнения и файл, как у обычной отправки.
 */
export type PendingAttempt =
  | {
      kind: 'start'
      chatId: string | null
      session: string | null
      input: StartRunInput
      key: string
      field: string
      file: AttemptFile | null
      cards: object | null
      fromField: boolean
      cause: AttemptCause
      /** Строка списания 03б (SMY-494): досылка кладёт её запуску так же, как старт. */
      notice?: string
    }
  | {
      kind: 'edit'
      chatId: string
      session: string | null
      artifactId: string
      text: string
      key: string
      cause: AttemptCause
    }

/**
 * На экране — карточки уточнений, чьи ответы ушли с отправкой: тот же объект, а
 * не «какие-то карточки есть» (ворота ревью 6 и 7, reviewer и
 * ux-states-checker). Чужие — новые вопросы, второй экран того же чата — не её:
 * ни текста её они не держат, ни её удача их не снимает. Повтор сбоя ответов
 * не несёт и карточек не держит, даже записанных (ворота ревью 9). Принимает и
 * отправку, ещё не ставшую попыткой, — у экрана на ответе старта (`launch`,
 * `accepted`) правило то же, без копии.
 */
export function holdsCards(
  sent: { kind: 'start'; fromField: boolean; cards: object | null } | { kind: 'edit' },
  shownCards: object | null,
): boolean {
  return sent.kind === 'start' && sent.fromField && sent.cards != null && sent.cards === shownCards
}

/**
 * Что попытка держит для поля: текст и файл. Пока на экране её карточки
 * (`shownCards` — карточки экрана сейчас), в поле возвращается только набранное
 * (`field`; кнопка под уточнениями с SMY-619 уводит его, как отправка): ответы держат они, и в поле они
 * встали бы вторым экземпляром. Её карточек нет — сняты, чужие, продление
 * гостя, — и вернуться в поле должно всё ушедшее, вопрос с ответами
 * (`sentText`), иначе ответы пропали бы вместе с карточками (ворота ревью 8,
 * reviewer). Повтор сбоя (`fromField` нет) — кнопка в ленте: в поле нечего.
 */
export function fieldOf(
  attempt: PendingAttempt,
  shownCards: object | null = null,
): { text: string; file: AttemptFile | null } {
  if (attempt.kind === 'edit') return { text: attempt.text, file: null }
  if (!attempt.fromField) return { text: attempt.field, file: attempt.file }
  return { text: holdsCards(attempt, shownCards) ? attempt.field : sentText(attempt.input), file: attempt.file }
}

/**
 * Что ушло из поля с этой отправкой — снимается из него, когда попытка встала
 * на экран или ушла на паузу: набранное и файл, у правки — её текст. Не то,
 * что вернётся при отказе (`fieldOf`): без своих карточек на экране там вопрос
 * с ответами, которого в поле нет, и набранное осталось бы в поле — в
 * кабинете уже другого чата, откуда его отправили бы второй раз (ворота ревью
 * 9, reviewer и ux-states-checker).
 */
export function leftField(attempt: PendingAttempt): { text: string; file: AttemptFile | null } {
  return attempt.kind === 'edit' ? { text: attempt.text, file: null } : { text: attempt.field, file: attempt.file }
}

/**
 * Что показывает пузырь попытки, пока исход неизвестен: то, что ушло на
 * сервер. В поле её текста нет — поле закрыто и остаётся человеку, а в
 * кабинете оно одно на все чаты и, вернувшись в чат, человек застал бы в нём
 * набранное в другом (ворота ревью 5, ux-states-checker).
 */
export function shownText(attempt: PendingAttempt): string {
  return attempt.kind === 'edit' ? attempt.text : sentText(attempt.input)
}

/**
 * Попытка, вернувшаяся с паузы. Новым объектом: досылка экрана обязана
 * начаться заново. Поле она не переписывает: что вернётся в поле, решает
 * `fieldOf` по карточкам на экране в момент возврата. Переписанное поле
 * помнило бы первый экран, забравший попытку, и ответы под своими карточками
 * второго встали бы дважды (ворота ревью 7, reviewer).
 */
export function fromPause(attempt: PendingAttempt): PendingAttempt {
  return { ...attempt }
}

/**
 * Пузыри отправленного, которых лента ещё не показывает. Пузырь — вопрос,
 * записанный сервером, но ещё не перечитанный; как только лента его принесла,
 * пузырь лишний. После досылки снимать все пузыри нельзя: у экрана, забравшего
 * попытку с паузы, бывают свои — вопрос к его уточнениям, — и он пропадал без
 * следа (ворота ревью 8, reviewer). Сверка — с вопросами человека, а не с
 * любым сообщением ленты. Конец запуска пузыри пока снимает все (`finished` и
 * `lost` в экранах, SMY-475); вопрос кнопки под уточнениями от этого не
 * зависит — он хранится с карточками.
 */
export function unseenBubbles(
  sent: readonly string[],
  feed: { messages: readonly { role: string; body: string }[] },
): string[] {
  return sent.filter((t) => !feed.messages.some((m) => m.role === 'user' && m.body.trim() === t.trim()))
}

/** Чем вернувшийся текст отделён от набранного в поле — одно правило с заготовкой «Изменить» (ядро). */
const AFTER_DRAFT = FIELD_AFTER_DRAFT

/**
 * Стоит ли в поле всё ещё отправленный текст. Поле кабинета одно на все чаты и
 * едет за человеком: снимать из него можно только своё — набранное после ухода
 * остаётся. Точным совпадением: укороченный или дописанный текст — уже новый.
 */
export function holdsSent(now: string, sent: string): boolean {
  return now.trim() !== '' && now.trim() === sent.trim()
}

/**
 * Поле после снятия своего: отправленный текст уходит, набранное остаётся. И
 * вернувшийся после набранного (`backInField`) — тоже: дошёл он со второго
 * раза, а набранное человек не отправлял.
 */
export function withoutSent(now: string, sent: string): string {
  if (holdsSent(now, sent)) return ''
  const tail = `${AFTER_DRAFT}${sent.trim()}`
  const end = now.trimEnd()
  return sent.trim() !== '' && end.endsWith(tail) ? end.slice(0, -tail.length) : now
}

/** Файл в поле после снятия своего: тот же файл уходит, другой остаётся. */
export function withoutSentFile(now: AttemptFile | null, sent: AttemptFile | null): AttemptFile | null {
  return now != null && sent != null && now.id === sent.id ? null : now
}

/**
 * Поле, в которое вернулась попытка — при отказе и когда сервер ничего не
 * запустил: её текст и файл. Набранное не затирается — в кабинете поле одно на
 * все чаты, и там бывает текст, набранный в другом, — а вернувшийся текст
 * встаёт после него новым абзацем: потерять нельзя ни то, ни другое (ворота
 * ревью 5, ux-states-checker). Уже стоящий в поле второй раз не дописывается.
 * Файл в поле один: занят другим — вернувшийся остаётся за человеком.
 * `shownCards` — карточки уточнений на экране, как у `fieldOf`.
 */
export function backInField(
  now: { text: string; file: AttemptFile | null },
  attempt: PendingAttempt,
  shownCards: object | null = null,
): { text: string; file: AttemptFile | null } {
  const back = fieldOf(attempt, shownCards)
  // Правило поля — в ядре, одно с заготовкой «Изменить» (SMY-497).
  return { text: appendToField(now.text, back.text), file: now.file ?? back.file }
}

export function createAttemptDesk() {
  /**
   * Очередь на чат, а не одна попытка: экран, у которого своя попытка ещё в
   * пути, мог забрать отсюда чужую — и своя, встав следом на паузу, затёрла бы
   * её (ворота ревью 5, reviewer). Экран показывает одну, остальные ждут здесь.
   */
  const parked = new Map<string, PendingAttempt[]>()
  /**
   * Удалённые чаты. Экран открытого чата уходит с него уже после удаления — и
   * поставил бы попытку на паузу снова, у чата, которого нет; вкладка
   * переспрашивала бы о ней до перезагрузки (ux-writer, 06.10.2026).
   */
  const gone = new Set<string>()
  /** Попытки, которые экраны показывают сейчас, — по экрану. */
  const shown = new Map<symbol, { user: string; attempt: PendingAttempt }>()
  const listeners = new Set<(user: string, chatId: string) => void>()
  const slot = (user: string, chatId: string): string => `${user}:${chatId}`

  return {
    /**
     * Поставить попытку человека на паузу у её чата. Попытку нового чата или
     * удалённого поставить некуда — `false`, и экран её снимает; попытку,
     * ушедшую другим сеансом (`session` — сеанс сейчас), — тоже: она не этого
     * человека (ворота ревью 5, security). Экраны этого чата узнают о ней сразу
     * (`subscribe`) — уже положенной: в приложении экран того же чата бывает
     * открыт поверх того, где она шла, а в кабинете экран снимается и встаёт
     * заново при уходе в раздел и возврате.
     */
    park(user: string, attempt: PendingAttempt, session: string | null): boolean {
      if (attempt.chatId == null || gone.has(attempt.chatId) || attempt.session !== session) return false
      const key = slot(user, attempt.chatId)
      parked.set(key, [...(parked.get(key) ?? []), attempt])
      for (const listener of listeners) listener(user, attempt.chatId)
      return true
    },

    /**
     * Забрать попытку чата, стоявшую на паузе, — только тому же человеку и его
     * же сеансу, по одной и в порядке ухода. Попытки другого сеанса снимаются,
     * не отдаваясь.
     */
    take(user: string, chatId: string, session: string | null): PendingAttempt | null {
      const key = slot(user, chatId)
      const [attempt = null, ...rest] = (parked.get(key) ?? []).filter((a) => a.session === session)
      if (rest.length > 0) parked.set(key, rest)
      else parked.delete(key)
      return attempt
    },

    /** Экран показывает попытку (или перестал): вкладка не закроется молча и с ней. */
    show(screen: symbol, user: string, attempt: PendingAttempt | null): void {
      if (attempt == null) shown.delete(screen)
      else shown.set(screen, { user, attempt })
    },

    /** Есть ли у человека живая попытка — на экране или на паузе. */
    has(user: string): boolean {
      for (const { user: who } of shown.values()) if (who === user) return true
      for (const key of parked.keys()) if (key.startsWith(`${user}:`)) return true
      return false
    },

    /** Чат удалён — его попытке возвращаться некуда, и на паузу она больше не встаёт. */
    forgetChat(chatId: string): void {
      gone.add(chatId)
      for (const key of [...parked.keys()]) if (key.endsWith(`:${chatId}`)) parked.delete(key)
    },

    /** Сменился человек — выход, вход, продление гостя: прежнее не его. */
    clear(): void {
      parked.clear()
      shown.clear()
      gone.clear()
    },

    subscribe(listener: (user: string, chatId: string) => void): () => void {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export type AttemptDesk = ReturnType<typeof createAttemptDesk>

/** Склад вкладки или приложения: один на процесс. */
export const attempts = createAttemptDesk()

/** Часы попыток: свои в проверках, настоящие на экране. */
export interface ReplayClock {
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(id: unknown): void
  setInterval(fn: () => void, ms: number): unknown
  clearInterval(id: unknown): void
}

const realClock: ReplayClock = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id as ReturnType<typeof setInterval>),
}

/**
 * Запрос со сроком `ATTEMPT_WAIT_MS` — у первой попытки и у каждой досылки
 * один: повисший старт иначе держал бы «Читаю задачу» и запертое окно
 * подтверждения, пока системе не надоест (ворота ревью 4, ux-states-checker).
 * Не дождался — `AttemptTimeout`. `stop` — своя остановка снаружи: оборванный
 * ею запрос отвечает своим отказом (`AbortError`), а не сроком.
 */
export async function withDeadline<T>(
  send: (signal: AbortSignal) => Promise<T>,
  clock: Pick<ReplayClock, 'setTimeout' | 'clearTimeout'> = realClock,
  stop?: AbortSignal,
): Promise<T> {
  const one = new AbortController()
  let timedOut = false
  const deadline = clock.setTimeout(() => {
    timedOut = true
    one.abort()
  }, ATTEMPT_WAIT_MS)
  const halt = (): void => one.abort()
  stop?.addEventListener('abort', halt)
  try {
    return await send(one.signal)
  } catch (e) {
    throw timedOut ? new AttemptTimeout() : e
  } finally {
    clock.clearTimeout(deadline)
    stop?.removeEventListener('abort', halt)
  }
}

/** Как досылать: запрос, исходы и откуда узнать, что сеть вернулась. */
export interface Replay<T> {
  /** Тот же запрос тем же ключом. */
  send(signal: AbortSignal): Promise<T>
  /** Ответ пришёл: продолжать, как у первой попытки. Досылка уже остановлена. */
  done(result: T): void
  /** Наш отказ: досылка остановлена. */
  refused(error: unknown): void
  /** Исход всё ещё неизвестен — повод для строки. */
  waiting(cause: AttemptCause): void
  /** Подписка на возврат сети; возвращает отписку. */
  onOnline(listener: () => void): () => void
  /**
   * Жив ли сеанс, которым ушла попытка. Сменился — досылка останавливается и
   * зовёт `lost`: до отправки — чтобы не уйти чужим токеном (ворота 5), и по
   * ответу — досылка, ушедшая прежним сеансом, бывает в пути до срока
   * попытки, и её ответ под новым не встаёт ни запуском, ни текстом в поле
   * (ворота 6, security).
   */
  alive?: () => boolean
  lost?: () => void
  /**
   * Ошибка в продолжении экрана (`done`, `refused`, `waiting`). Без этого
   * выхода она всплывает необработанной — до консоли и красного экрана
   * разработки, но не глотается молча: строка над полем к этому моменту уже
   * снята (ворота ревью 5, reviewer).
   */
  crashed?: (error: unknown) => void
}

/**
 * Досылать попытку, пока не придёт ответ: сразу, при возврате сети и каждые
 * `ATTEMPT_STEP_MS`, в полёте не больше одной и каждая не дольше
 * `ATTEMPT_WAIT_MS`. Возвращает остановку: после неё не приходит ни один исход,
 * даже если ответ уже в пути, — экран ушёл, и чужое поле трогать нельзя.
 *
 * Исходы зовутся уже вне ожидания запроса: ошибка в продолжении экрана не
 * глотается вместе с отказом запроса, а уходит в `crashed` или всплывает
 * (ворота ревью 5, reviewer).
 */
export function startReplay<T>(replay: Replay<T>, clock: ReplayClock = realClock): () => void {
  let stopped = false
  let flying = false
  const halt = new AbortController()

  const attempt = async (): Promise<void> => {
    if (stopped || flying) return
    if (replay.alive != null && !replay.alive()) {
      stop()
      replay.lost?.()
      return
    }
    flying = true
    let outcome: { ok: true; result: T } | { ok: false; error: unknown }
    try {
      outcome = { ok: true, result: await withDeadline((signal) => replay.send(signal), clock, halt.signal) }
    } catch (error) {
      outcome = { ok: false, error }
    } finally {
      flying = false
    }
    if (stopped) return
    if (replay.alive != null && !replay.alive()) {
      stop()
      replay.lost?.()
      return
    }
    try {
      if (outcome.ok) {
        stop()
        replay.done(outcome.result)
        return
      }
      const cause = outcomeCause(outcome.error)
      if (cause == null) {
        stop()
        replay.refused(outcome.error)
        return
      }
      replay.waiting(cause)
    } catch (error) {
      if (replay.crashed == null) throw error
      replay.crashed(error)
    }
  }

  const tick = clock.setInterval(() => void attempt(), ATTEMPT_STEP_MS)
  const offline = replay.onOnline(() => void attempt())
  function stop(): void {
    if (stopped) return
    stopped = true
    clock.clearInterval(tick)
    offline()
    halt.abort()
  }
  void attempt()
  return stop
}
